import { supabase } from '@/integrations/supabase/client';

export interface InvestorHolding {
  user_id: string;
  company_id: string;
  shareholder_id: string;
  shareholder_name: string;
  share_class: string;
  shares: number;
}

export interface InvestorCertificate {
  user_id: string;
  company_id: string;
  certificate_id: string;
  cert_number: string;
  share_class: string;
  shares: number;
  status: string;
  issue_date: string;
  pdf_url?: string;
}

export interface InvestorValuation {
  user_id: string;
  company_id: string;
  total_shares: number;
  fmv_per_share: number | null;
  as_of_date: string | null;
  implied_value: number | null;
}

export async function getInvestorHoldings(userId: string): Promise<InvestorHolding[]> {
  const { data, error } = await supabase
    .from('investor_holdings')
    .select('*')
    .eq('user_id', userId);

  if (error) throw error;
  return data || [];
}

export async function getInvestorCertificates(userId: string): Promise<InvestorCertificate[]> {
  const { data, error } = await supabase
    .from('investor_certificates')
    .select('*')
    .eq('user_id', userId);

  if (error) throw error;
  return data || [];
}

export async function getInvestorValuations(userId: string): Promise<InvestorValuation[]> {
  const { data, error } = await supabase
    .from('investor_valuation_summary')
    .select('*')
    .eq('user_id', userId);

  if (error) throw error;
  return data || [];
}

export interface InvestorReport {
  id: string;
  company_id: string;
  period_start: string | null;
  period_end: string | null;
  report_url: string | null;
  created_at: string;
}

export interface InvestorMessage {
  id: string;
  company_id: string;
  sender_id: string;
  sender_role: string;
  recipient_role: string;
  subject: string;
  body: string;
  is_read: boolean;
  created_at: string;
}

export interface PortfolioSummary {
  totalValue: number;
  companiesCount: number;
  topHolding: {
    company_name: string;
    shares: number;
    implied_value: number;
  } | null;
}

export interface InvestorPortalData {
  holdings: InvestorHolding[];
  certificates: InvestorCertificate[];
  valuations: InvestorValuation[];
  reports: Map<string, InvestorReport[]>;
  messages: Map<string, InvestorMessage[]>;
  portfolioSummary: PortfolioSummary | null;
}

export async function getInvestorReports(userId: string): Promise<InvestorReport[]> {
  const { data, error } = await supabase
    .from('investor_reports')
    .select('*')
    .order('created_at', { ascending: false });

  if (error) throw error;
  return data || [];
}

export async function getInvestorMessages(companyId: string): Promise<InvestorMessage[]> {
  const { data, error } = await supabase
    .from('investor_messages')
    .select('*')
    .eq('company_id', companyId)
    .order('created_at', { ascending: false });

  if (error) throw error;
  return data || [];
}

export async function getPortfolioSummary(userId: string): Promise<PortfolioSummary | null> {
  const { data, error } = await supabase.rpc('investor_portfolio_overview', {
    p_user_id: userId,
  });

  if (error) {
    console.error('Portfolio summary error:', error);
    return null;
  }

  // Transform RPC response to PortfolioSummary format
  const totalValue = (data || []).reduce(
    (sum: number, item: any) => sum + (item.implied_value || 0),
    0
  );
  const companiesCount = data?.length || 0;
  const topHolding = data?.[0]
    ? {
        company_name: data[0].company_name,
        shares: data[0].shares,
        implied_value: data[0].implied_value,
      }
    : null;

  return {
    totalValue,
    companiesCount,
    topHolding,
  };
}

export async function getInvestorPortalData(userId: string): Promise<InvestorPortalData> {
  const [holdings, certificates, valuations, allReports, portfolioSummary] = await Promise.all([
    getInvestorHoldings(userId),
    getInvestorCertificates(userId),
    getInvestorValuations(userId),
    getInvestorReports(userId),
    getPortfolioSummary(userId),
  ]);

  // Get unique company IDs from holdings
  const companyIds = [...new Set(holdings.map((h) => h.company_id))];

  // Fetch messages for each company
  const messagesPromises = companyIds.map((id) => getInvestorMessages(id));
  const messagesArrays = await Promise.all(messagesPromises);

  // Organize reports and messages by company
  const reportsByCompany = new Map<string, InvestorReport[]>();
  const messagesByCompany = new Map<string, InvestorMessage[]>();

  allReports.forEach((report) => {
    if (!reportsByCompany.has(report.company_id)) {
      reportsByCompany.set(report.company_id, []);
    }
    reportsByCompany.get(report.company_id)!.push(report);
  });

  companyIds.forEach((companyId, index) => {
    messagesByCompany.set(companyId, messagesArrays[index]);
  });

  return {
    holdings,
    certificates,
    valuations,
    reports: reportsByCompany,
    messages: messagesByCompany,
    portfolioSummary,
  };
}

export function subscribeToInvestorUpdates(
  callback: () => void,
  userId: string,
  companyIds: string[]
) {
  // Don't subscribe if no companies to watch
  if (companyIds.length === 0) {
    console.log('[InvestorPortal] No companies to watch, skipping realtime subscription');
    return {
      channel: null as any,
      cleanup: () => {},
    };
  }

  console.log('[InvestorPortal] Setting up realtime subscription for companies:', companyIds);

  // Debounce callback to prevent rapid successive updates
  let timeoutId: ReturnType<typeof setTimeout>;
  const debouncedCallback = () => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(callback, 2000); // Wait 2s before triggering refresh
  };

  // Create filter for company IDs
  const companyFilter = `company_id=in.(${companyIds.join(',')})`;

  // Create unique channel name based on company IDs to prevent overlapping subscriptions
  const channelName = `investor_portal_${companyIds.sort().join('_')}`;

  const channel = supabase
    .channel(channelName)
    .on(
      'postgres_changes',
      {
        event: 'INSERT',
        schema: 'public',
        table: 'share_certificates',
        filter: `user_id=eq.${userId}`, // Only trigger for current user's certificates
      },
      debouncedCallback
    )
    .on(
      'postgres_changes',
      {
        event: 'INSERT',
        schema: 'public',
        table: 'ledger_events',
        filter: companyFilter, // Only watch companies the investor has holdings in
      },
      debouncedCallback
    )
    .on(
      'postgres_changes',
      {
        event: 'INSERT',
        schema: 'public',
        table: 'valuations',
        filter: companyFilter, // Only watch companies the investor has holdings in
      },
      debouncedCallback
    )
    .subscribe();

  // Return both channel and cleanup function
  return {
    channel,
    cleanup: () => clearTimeout(timeoutId),
  };
}
